import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Copy, Trash2, UserPlus, Users, Link as LinkIcon } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

const LS_ORG = "aevum_active_org_v1";

export default function MembersPage() {
  const [orgs, setOrgs] = useState([]);
  const activeId = localStorage.getItem(LS_ORG);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [role, setRole] = useState("reviewer");
  const [loading, setLoading] = useState(true);
  const active = orgs.find((o) => o.id === activeId);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const o = await api.get("/orgs");
      setOrgs(o.data);
      const aid = localStorage.getItem(LS_ORG) || o.data.find((x) => !x.personal)?.id || o.data[0]?.id;
      if (!aid) return;
      const [m, i] = await Promise.all([
        api.get(`/orgs/${aid}/members`),
        api.get(`/orgs/${aid}/invitations`).catch(() => ({ data: [] })),
      ]);
      setMembers(m.data); setInvites(i.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const createInvite = async () => {
    try {
      const r = await api.post(`/orgs/${activeId}/invitations`, { role });
      const url = `${window.location.origin}/invite/${r.data.token}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success("Invite link created & copied");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const copyLink = (t) => {
    const url = `${window.location.origin}/invite/${t}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Link copied"));
  };

  const revoke = async (id) => {
    await api.delete(`/invitations/${id}`);
    toast.success("Invite revoked");
    load();
  };
  const remove = async (uid, name) => {
    if (!confirm(`Remove ${name} from this workspace?`)) return;
    try { await api.delete(`/orgs/${activeId}/members/${uid}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="text-sm text-surface-onVariant">Loading workspace…</div>;
  if (!active) return <div className="text-sm text-surface-onVariant">No active workspace.</div>;
  if (active.personal) {
    return (
      <div className="animate-fade-in-up max-w-2xl">
        <h2 className="text-2xl md:text-3xl font-medium text-surface-on tracking-tight">Members</h2>
        <div className="mt-6 rounded-hero bg-surface-container p-10 text-center">
          <Users className="w-8 h-8 mx-auto text-surface-onVariant" />
          <p className="text-sm text-surface-onVariant mt-3">
            "{active.name}" is a personal workspace — it doesn't have members. Create a team workspace from the switcher to invite others.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in-up max-w-3xl">
      <h2 className="text-2xl md:text-3xl font-medium text-surface-on tracking-tight">Members</h2>
      <p className="text-sm text-surface-onVariant mt-1">{active.name} · {members.length} member{members.length !== 1 ? "s" : ""}</p>

      {/* Invite */}
      <div className="mt-6 rounded-hero bg-surface-container p-5">
        <div className="text-sm font-medium text-surface-on inline-flex items-center gap-2"><UserPlus className="w-4 h-4 text-primary" /> Invite by link</div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger data-testid="invite-role" className="w-40 rounded-full bg-surface border border-outline-variant h-10 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-2xl bg-surface">
              <SelectItem value="reviewer">Reviewer</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
          <button data-testid="create-invite" onClick={createInvite}
            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground text-sm font-medium px-4 py-2 active:scale-[0.98] hover:brightness-110 transition-[transform,filter] duration-200 ease-md">
            <LinkIcon className="w-4 h-4" /> Generate link
          </button>
        </div>
        {invites.length > 0 && (
          <ul className="mt-4 space-y-2" data-testid="invites-list">
            {invites.map((i) => (
              <li key={i.id} className="flex items-center gap-3 bg-surface rounded-2xl px-4 py-3 border border-outline-variant">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-surface-on truncate font-mono">/invite/{i.token.slice(0, 8)}…</div>
                  <div className="text-[10px] text-surface-onVariant">role: {i.role} · expires {new Date(i.expires_at).toLocaleDateString()}</div>
                </div>
                <button onClick={() => copyLink(i.token)} className="p-1.5 rounded-full text-surface-onVariant hover:bg-primary/8 hover:text-primary" aria-label="Copy link"><Copy className="w-4 h-4" /></button>
                <button onClick={() => revoke(i.id)} className="p-1.5 rounded-full text-surface-onVariant hover:bg-danger-container hover:text-danger" aria-label="Revoke"><Trash2 className="w-4 h-4" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Members list */}
      <div className="mt-6 rounded-hero bg-surface-container overflow-hidden">
        <ul className="divide-y divide-outline-variant" data-testid="members-list">
          {members.map((m) => (
            <li key={m.user_id} className="px-5 py-4 flex items-center gap-4">
              <div className="w-9 h-9 rounded-full bg-tertiary-container text-tertiary-onContainer flex items-center justify-center font-medium text-sm">
                {m.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-surface-on truncate">{m.name}</div>
                <div className="text-xs text-surface-onVariant truncate">{m.email}</div>
              </div>
              <span className={cn("text-[10px] font-medium tracking-wider uppercase px-2 py-0.5 rounded-full",
                m.role === "owner" ? "bg-primary-container text-primary-onContainer" :
                m.role === "admin" ? "bg-tertiary-container text-tertiary-onContainer" :
                "bg-surface-containerHigh text-surface-onVariant")}>
                {m.role}
              </span>
              {m.role !== "owner" && (
                <button onClick={() => remove(m.user_id, m.name)} className="p-1.5 rounded-full text-surface-onVariant hover:bg-danger-container hover:text-danger" aria-label="Remove"><Trash2 className="w-4 h-4" /></button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
